import React, {useEffect, useReducer} from 'react';

import {assertAndContinue} from 'Asserts';
import notifications from 'notifications';
import {FulfillmentStatus} from 'requests/fulfillments/FulfillmentConstants';
import {
  RequestDetailsFulfillmentGraphQL,
  RequestDetailsRequestGraphQL,
  RequestDetailsSampleGroupGraphQL,
  RequestDetailsTaskGraphQL,
  RequestFullfillableStatuses,
} from 'requests/RequestConstants';
import getSampleGroupIdsByRequestTaskId, {
  SampleGroupIdsByRequestTaskIdMap,
} from 'workflows/samples/getSampleGroupIdsByRequestTaskId';
import {PreparedUISampleGroup} from 'workflows/samples/PreparedSampleGroupConstants';
import prepareSampleGroupsForServer from 'workflows/samples/prepareSampleGroupsForServer';
import prepareSampleGroupsGraphQLForTable from 'workflows/samples/prepareSampleGroupsGraphQLForTable';
import {SampleGroupUpdate} from 'workflows/WorkflowConstants';

import {RequestTaskSchemaGraphQL} from '../../../graphql-fragments/requests/schemas/__generated/RequestTaskSchemaGraphQL';
import EditableRequestSampleTable from './EditableRequestSampleTable';

function getFulfillmentStatusBySampleGroupId(
  fulfillments: Array<RequestDetailsFulfillmentGraphQL>
): RequestFullfillableStatuses {
  const fulfillableStatuses: RequestFullfillableStatuses = {};
  fulfillments.forEach((fulfillment) => {
    if (fulfillment.sampleGroup != null && fulfillment.task != null) {
      const sampleGroupStatuses: {[requestTaskId: string]: FulfillmentStatus} =
        fulfillableStatuses[fulfillment.sampleGroup.id] ?? {};
      sampleGroupStatuses[fulfillment.task.id] = fulfillment.status as FulfillmentStatus;
      fulfillableStatuses[fulfillment.sampleGroup.id] = sampleGroupStatuses;
    }
  });
  return fulfillableStatuses;
}

interface DraftableRequestSampleTableProps {
  isWritable: boolean;
  request: RequestDetailsRequestGraphQL;
  requestTaskSchemasById: {[requestTaskSchemaId: string]: RequestTaskSchemaGraphQL};
  onSaveSampleGroups: ({
    sampleGroupIdsByRequestTaskId,
    sampleGroupsForServer,
  }: {
    sampleGroupIdsByRequestTaskId: SampleGroupIdsByRequestTaskIdMap;
    sampleGroupsForServer: Array<SampleGroupUpdate>;
  }) => Promise<void>;
}

interface EditingRequestSampleTableState {
  isEditing: true;
  isSaving: boolean;
  draftSampleGroups: Array<PreparedUISampleGroup>;
  draftSampleGroupIdsByRequestTaskId: SampleGroupIdsByRequestTaskIdMap;
}

interface RestingRequestSampleTableState {
  isEditing: false;
  isSaving: boolean;
  draftSampleGroups: null;
  draftSampleGroupIdsByRequestTaskId: null;
}

type DraftableRequestSampleTableState = EditingRequestSampleTableState | RestingRequestSampleTableState;

const INITIAL_STATE: RestingRequestSampleTableState = {
  isEditing: false,
  isSaving: false,
  draftSampleGroups: null,
  draftSampleGroupIdsByRequestTaskId: null,
};

enum DraftableRequestSampleTableActionTypes {
  RESET_STATE = 'RESET_STATE',
  SET_IS_EDITING = 'SET_IS_EDITING',
  SET_IS_SAVING = 'SET_IS_SAVING',
  SET_DRAFT_SAMPLE_GROUPS = 'SET_DRAFT_SAMPLE_GROUPS',
  SET_DRAFT_SAMPLE_GROUP_IDS_BY_REQUEST_TASK_ID = 'SET_DRAFT_SAMPLE_GROUP_IDS_BY_REQUEST_TASK_ID',
  MERGE_SAMPLE_GROUP_PROPS = 'MERGE_SAMPLE_GROUP_PROPS',
}

interface ResetStateAction {
  type: DraftableRequestSampleTableActionTypes.RESET_STATE;
}

interface SetIsEditingAction {
  type: DraftableRequestSampleTableActionTypes.SET_IS_EDITING;
  payload: {
    sampleGroups: Array<RequestDetailsSampleGroupGraphQL>;
    tasks: Array<RequestDetailsTaskGraphQL>;
  };
}

interface SetIsSavingAction {
  type: DraftableRequestSampleTableActionTypes.SET_IS_SAVING;
}

interface SetDraftSampleGroupsAction {
  type: DraftableRequestSampleTableActionTypes.SET_DRAFT_SAMPLE_GROUPS;
  payload: Pick<EditingRequestSampleTableState, 'draftSampleGroups'>;
}

interface SetDraftSampleGroupIdsByRequestTaskIdAction {
  type: DraftableRequestSampleTableActionTypes.SET_DRAFT_SAMPLE_GROUP_IDS_BY_REQUEST_TASK_ID;
  payload: Pick<EditingRequestSampleTableState, 'draftSampleGroupIdsByRequestTaskId'>;
}

interface MergeSampleGroupPropsAction {
  type: DraftableRequestSampleTableActionTypes.MERGE_SAMPLE_GROUP_PROPS;
  payload: {tasks: Array<RequestDetailsTaskGraphQL>};
}

type DraftableRequestSampleTableActions =
  | ResetStateAction
  | SetIsEditingAction
  | SetIsSavingAction
  | SetDraftSampleGroupsAction
  | SetDraftSampleGroupIdsByRequestTaskIdAction
  | MergeSampleGroupPropsAction;

function reducer(
  state: DraftableRequestSampleTableState,
  action: DraftableRequestSampleTableActions
): DraftableRequestSampleTableState {
  switch (action.type) {
    case DraftableRequestSampleTableActionTypes.RESET_STATE:
      return INITIAL_STATE;
    case DraftableRequestSampleTableActionTypes.SET_IS_EDITING: {
      const draftSampleGroups = prepareSampleGroupsGraphQLForTable(action.payload.sampleGroups);
      const draftSampleGroupIdsByRequestTaskId = prepareSampleGroupIdsByRequestTaskId(
        getSampleGroupIdsByRequestTaskId(action.payload.tasks),
        action.payload.tasks
      );
      getSampleGroupIdsByRequestTaskId(action.payload.tasks);
      return {...state, isEditing: true, draftSampleGroups, draftSampleGroupIdsByRequestTaskId};
    }
    case DraftableRequestSampleTableActionTypes.SET_IS_SAVING:
      return {...state, isSaving: true};
    case DraftableRequestSampleTableActionTypes.SET_DRAFT_SAMPLE_GROUPS: {
      const {draftSampleGroups} = action.payload;
      return state.isEditing ? {...state, draftSampleGroups} : state;
    }
    case DraftableRequestSampleTableActionTypes.SET_DRAFT_SAMPLE_GROUP_IDS_BY_REQUEST_TASK_ID: {
      const {draftSampleGroupIdsByRequestTaskId} = action.payload;
      return state.isEditing ? {...state, draftSampleGroupIdsByRequestTaskId} : state;
    }
    case DraftableRequestSampleTableActionTypes.MERGE_SAMPLE_GROUP_PROPS: {
      if (state.isEditing) {
        const inboundSampleGroupIdsByRequestTaskId = getSampleGroupIdsByRequestTaskId(action.payload.tasks);
        const draftSampleGroupIdsByRequestTaskId = Object.keys(inboundSampleGroupIdsByRequestTaskId).reduce(
          (memo: SampleGroupIdsByRequestTaskIdMap, taskId: string) => {
            memo[taskId] =
              state.draftSampleGroupIdsByRequestTaskId[taskId] ??
              inboundSampleGroupIdsByRequestTaskId[taskId];
            return memo;
          },
          {}
        );
        return {...state, draftSampleGroupIdsByRequestTaskId};
      } else {
        return state;
      }
    }
    default:
      return state;
  }
}

function prepareSampleGroupIdsByRequestTaskId(
  s: SampleGroupIdsByRequestTaskIdMap,
  tasks: Array<RequestDetailsTaskGraphQL>
): SampleGroupIdsByRequestTaskIdMap {
  return tasks.reduce((memo, task) => (s[task.id] == null ? {...memo, [task.id]: []} : memo), s);
}

export default function DraftableRequestSampleTable(props: DraftableRequestSampleTableProps): JSX.Element {
  const [state, dispatch] = useReducer(reducer, INITIAL_STATE);

  useEffect(() => {
    dispatch({
      type: DraftableRequestSampleTableActionTypes.MERGE_SAMPLE_GROUP_PROPS,
      payload: {tasks: props.request.tasks},
    });
  }, [props.request.sampleGroups, props.request.tasks]);

  // Ensure that if an incoming new task arrives, the draft map contains it. The useEffect hook will
  // appropriately merge the underlying map after a render cycle completes
  const sampleGroupIdsByRequestTaskId = state.isEditing
    ? prepareSampleGroupIdsByRequestTaskId(state.draftSampleGroupIdsByRequestTaskId, props.request.tasks)
    : getSampleGroupIdsByRequestTaskId(props.request.tasks);

  const sampleGroups = state.isEditing
    ? state.draftSampleGroups
    : prepareSampleGroupsGraphQLForTable(props.request.sampleGroups);

  if (state.draftSampleGroups != null) {
    assertAndContinue(
      state.draftSampleGroups.every((sampleGroup: PreparedUISampleGroup) => sampleGroup.cid != null),
      'Expected sample groups to have cids'
    );
  }

  const resetState = (): void => {
    dispatch({type: DraftableRequestSampleTableActionTypes.RESET_STATE});
  };

  const handleSave = async (): Promise<void> => {
    try {
      dispatch({type: DraftableRequestSampleTableActionTypes.SET_IS_SAVING});
      const sampleGroupsForServer = prepareSampleGroupsForServer(sampleGroups);
      await props.onSaveSampleGroups({sampleGroupIdsByRequestTaskId, sampleGroupsForServer});
      notifications.success('Updated sample groups successfully.');
    } finally {
      resetState();
    }
  };

  const fulfillableStatuses = getFulfillmentStatusBySampleGroupId(props.request.fulfillments);

  return (
    <EditableRequestSampleTable
      requestId={props.request.id}
      canToggleEditMode={true}
      isWritable={props.isWritable}
      isEditing={state.isEditing}
      isSaving={state.isSaving}
      requestTaskSchemasById={props.requestTaskSchemasById}
      requestTasks={props.request.tasks}
      sampleGroupIdsByRequestTaskId={sampleGroupIdsByRequestTaskId}
      sampleGroupSchema={props.request.schema.sampleGroupSchema!}
      sampleGroups={sampleGroups}
      fulfillableStatuses={fulfillableStatuses}
      onCancelEdit={() => {
        resetState();
      }}
      onEdit={() => {
        dispatch({
          type: DraftableRequestSampleTableActionTypes.SET_IS_EDITING,
          payload: {sampleGroups: props.request.sampleGroups, tasks: props.request.tasks},
        });
      }}
      onSave={handleSave}
      onUpdateSampleGroups={(sampleGroups) => {
        dispatch({
          type: DraftableRequestSampleTableActionTypes.SET_DRAFT_SAMPLE_GROUPS,
          payload: {draftSampleGroups: sampleGroups},
        });
      }}
      onUpdateSampleGroupIdsByRequestTaskId={(sampleGroupIdsByRequestId) => {
        dispatch({
          type: DraftableRequestSampleTableActionTypes.SET_DRAFT_SAMPLE_GROUP_IDS_BY_REQUEST_TASK_ID,
          payload: {draftSampleGroupIdsByRequestTaskId: sampleGroupIdsByRequestId},
        });
      }}
    />
  );
}
